/**
 * AnalysisShowcase — Premium renderer for strategic analysis / SWOT / consulting layouts.
 * Palette: indigo primary + rose risks + emerald strengths + cyan opportunities.
 * Migrated to Athena Presentation Design System (Phase 7.5).
 */
import React from 'react';
import { usePresentationBreakpoint, AthenaTip, ActionChips, SectionCard, HeroPhotoPanel, MetricStrip }
  from '../presentation-system';
import { base } from '../presentation-system/tokens/colors';
import { ProseText } from './ProseText';

const C = {
  accent:       '#818cf8',
  accentFade:   'rgba(129,140,248,0.1)',
  accentBorder: 'rgba(129,140,248,0.22)',
  rose:         '#f43f5e',
  roseFade:     'rgba(244,63,94,0.1)',
  roseBorder:   'rgba(244,63,94,0.22)',
  green:        '#34d399',
  greenFade:    'rgba(52,211,153,0.1)',
  greenBorder:  'rgba(52,211,153,0.22)',
  cyan:         '#22d3ee',
  cyanFade:     'rgba(34,211,238,0.1)',
  cyanBorder:   'rgba(34,211,238,0.22)',
  amber:        '#f59e0b',
  amberFade:    'rgba(245,158,11,0.1)',
  amberBorder:  'rgba(245,158,11,0.22)',
  card:         base.card,
  border:       base.border,
  text:         base.text,
  textMuted:    base.textMuted,
  textFaint:    base.textFaint,
};

const ANALYSIS_LABEL = {
  swot:       '🔬 ANÁLISE SWOT',
  market:     '📊 ANÁLISE DE MERCADO',
  financial:  '💰 ANÁLISE FINANCEIRA',
  strategic:  '♟️ ANÁLISE ESTRATÉGICA',
  general:    '🔍 ANÁLISE',
};

const _card = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, boxShadow: '0 2px 16px rgba(0,0,0,0.2)' };

function AnalysisHeroDesktop({ d }) {
  const typeLabel = ANALYSIS_LABEL[d.analysis_type] || ANALYSIS_LABEL.general;
  const titleFs   = (d.title || '').length > 38 ? 21 : (d.title || '').length > 24 ? 26 : 30;
  const METRICS   = [
    { icon: '🎯', value: d.subject    || 'Análise',      label: 'Objeto'   },
    { icon: '🔬', value: d.analysis_type || 'Estratégica', label: 'Tipo'   },
    { icon: '📌', value: d.objective  || 'Estratégico',  label: 'Objetivo' },
  ];

  return (
    <div style={{ ..._card, padding: 0, marginBottom: 18, overflow: 'hidden', display: 'flex', height: 260,
      background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
      boxShadow: '0 4px 40px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.03) inset',
    }}>
      <HeroPhotoPanel
        heroImage={d.hero_image}
        badge={typeLabel}
        accentColor={C.accent} accentFade={C.accentFade} accentBdr={C.accentBorder}
        placeholder="🔬" placeholderLabel="Análise"
        title={d.title}
      />
      <div style={{ flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
        <div>
          <h1 style={{ margin: '0 0 8px', fontSize: titleFs, fontWeight: 800, color: C.text, lineHeight: 1.2, letterSpacing: '-.02em',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.title}</h1>
          {d.subtitle && <p style={{ margin: '0 0 8px', fontSize: 13, color: base.textSec, lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.subtitle}</p>}
          {d.subject && <div style={{ fontSize: 13, color: C.accent, fontWeight: 600 }}>🎯 {d.subject}</div>}
        </div>
        <MetricStrip metrics={METRICS} accentColor={C.accent} />
      </div>
    </div>
  );
}

function AnalysisHeroMobile({ d }) {
  const typeLabel = ANALYSIS_LABEL[d.analysis_type] || ANALYSIS_LABEL.general;
  const METRICS   = [
    { icon: '🎯', value: d.subject    || 'Análise',      label: 'Objeto'   },
    { icon: '🔬', value: d.analysis_type || 'Estratégica', label: 'Tipo'   },
    { icon: '📌', value: d.objective  || 'Estratégico',  label: 'Objetivo' },
    { icon: '♟️', value: 'Análise',                      label: 'Formato'  },
  ];

  return (
    <div style={{ ..._card, padding: 0, marginBottom: 14, overflow: 'hidden',
      background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
      boxShadow: '0 4px 40px rgba(0,0,0,0.4)',
    }}>
      <div style={{ display: 'flex', minHeight: 202 }}>
        <div style={{ flex: 1, minWidth: 0, padding: '20px 10px 18px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <span style={{ display: 'inline-block', alignSelf: 'flex-start', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: C.accent, background: C.accentFade, border: `1px solid ${C.accentBorder}`, borderRadius: 5, padding: '2px 8px', marginBottom: 10 }}>{typeLabel}</span>
          {d.subject && <div style={{ fontSize: 12, color: C.accent, fontWeight: 600, marginBottom: 5 }}>🎯 {d.subject}</div>}
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.text, lineHeight: 1.25 }}>{d.title}</h1>
          {d.subtitle && <p style={{ margin: '6px 0 0', fontSize: 12, color: base.textSec, lineHeight: 1.4,
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.subtitle}</p>}
        </div>
        <HeroPhotoPanel
          heroImage={d.hero_image} isMobile
          accentColor={C.accent} accentFade={C.accentFade} accentBdr={C.accentBorder}
          placeholder="🔬"
          title={d.title}
        />
      </div>
      <div style={{ borderTop: `1px solid ${C.border}`, background: 'rgba(0,0,0,0.18)' }}>
        <MetricStrip metrics={METRICS} bordered accentColor={C.accent} />
      </div>
    </div>
  );
}

function SwotQuadrant({ title, items, color, fade, border, icon }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ background: fade, border: `1px solid ${border}`, borderRadius: 8, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color, marginBottom: 8 }}>{icon} {title}</div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 5 }}>
        {items.map((it, i) => (
          <li key={i} style={{ display: 'flex', gap: 7, fontSize: 13, color: C.text }}>
            <span style={{ color, flexShrink: 0, marginTop: 3 }}>•</span>
            <span style={{ lineHeight: 1.5 }}>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ItemList({ items, color }) {
  if (!items || items.length === 0) return null;
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((it, i) => (
        <li key={i} style={{ display: 'flex', gap: 8, fontSize: 14, color: C.text }}>
          <span style={{ color: color || C.accent, flexShrink: 0, marginTop: 3 }}>•</span>
          <span style={{ lineHeight: 1.5 }}>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function InsightCard({ text, index }) {
  return (
    <div style={{ background: C.accentFade, border: `1px solid ${C.accentBorder}`, borderRadius: 8, padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <div style={{ width: 22, height: 22, borderRadius: '50%', background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: '#0e1628', flexShrink: 0, marginTop: 1 }}>{index + 1}</div>
      <p style={{ margin: 0, fontSize: 13, color: C.text, lineHeight: 1.5 }}>{text}</p>
    </div>
  );
}

export default function AnalysisShowcase({ data, wp, onAction }) {
  const isMobile = usePresentationBreakpoint();
  const d = wp || data;
  if (!d) return null;

  const hasSwot = (d.strengths?.length || 0) + (d.weaknesses?.length || 0) + (d.opportunities?.length || 0) + (d.risks?.length || 0) > 0;

  return (
    <div style={{ color: C.text, fontFamily: 'inherit', lineHeight: 1.6, maxWidth: '100%' }}>
      {isMobile ? <AnalysisHeroMobile d={d} /> : <AnalysisHeroDesktop d={d} />}

      {d.panorama && (
        <SectionCard title="Panorama Geral" borderColor={C.accentBorder} style={{ marginBottom: 12 }}>
          <ProseText text={d.panorama} style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6, whiteSpace: 'pre-wrap' }} />
        </SectionCard>
      )}

      {hasSwot && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: C.textMuted, marginBottom: 10 }}>Matriz SWOT</div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
            <SwotQuadrant title="Forças"        items={d.strengths}     color={C.green}  fade={C.greenFade}  border={C.greenBorder}  icon="💪" />
            <SwotQuadrant title="Fraquezas"     items={d.weaknesses}    color={C.rose}   fade={C.roseFade}   border={C.roseBorder}   icon="⚠️" />
            <SwotQuadrant title="Oportunidades" items={d.opportunities} color={C.cyan}   fade={C.cyanFade}   border={C.cyanBorder}   icon="🚀" />
            <SwotQuadrant title="Ameaças"       items={d.risks}         color={C.amber}  fade={C.amberFade}  border={C.amberBorder}  icon="🔥" />
          </div>
        </div>
      )}

      {d.insights && d.insights.length > 0 && (
        <SectionCard title="Insights-chave" borderColor={C.accent} style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {d.insights.map((it, i) => <InsightCard key={i} text={it} index={i} />)}
          </div>
        </SectionCard>
      )}

      {d.recommendations && d.recommendations.length > 0 && (
        <SectionCard title="Recomendações" borderColor={C.green} style={{ marginBottom: 12 }}>
          <ItemList items={d.recommendations} color={C.green} />
        </SectionCard>
      )}

      {d.conclusion && (
        <div style={{ background: `linear-gradient(135deg, ${C.accentFade}, rgba(52,211,153,0.06))`, border: `1px solid ${C.accentBorder}`, borderRadius: 10, padding: '16px', marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: C.textMuted, marginBottom: 8 }}>Conclusão</div>
          <p style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6 }}>{d.conclusion}</p>
        </div>
      )}

      {(d.sections || []).map((s, i) => (
        <SectionCard key={i} title={s.title} borderColor={C.border} style={{ marginBottom: 10 }}>
          <ProseText text={s.content} style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6, whiteSpace: 'pre-wrap' }} />
        </SectionCard>
      ))}

      <AthenaTip tip={d.tip} accentColor={C.accent} style={{ marginBottom: 14 }} />
      <ActionChips chips={d.follow_up_chips} onAction={onAction} accentColor={C.accent} />
    </div>
  );
}
